import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

const COLLECTION_NAME = 'parrot-bookings';

export const BookingStatus = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  DISPUTED: 'disputed',
};

export class BookingsService {
  
  // Create a new booking
  static async create(bookingData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTION_NAME), {
        ...bookingData,
        status: BookingStatus.PENDING,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { id: docRef.id, ...bookingData, status: BookingStatus.PENDING };
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error;
    }
  }

  // Get booking by ID
  static async getById(bookingId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, bookingId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting booking:', error);
      throw error;
    }
  }

  // Get bookings by client ID
  static async getByClientId(clientId) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('clientId', '==', clientId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting client bookings:', error);
      throw error;
    }
  }

  // Get bookings by provider ID
  static async getByProviderId(providerId, status = null) {
    try {
      let q;
      if (status) {
        q = query(
          collection(db, COLLECTION_NAME),
          where('providerId', '==', providerId),
          where('status', '==', status),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          collection(db, COLLECTION_NAME),
          where('providerId', '==', providerId),
          orderBy('createdAt', 'desc')
        );
      }
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting provider bookings:', error);
      throw error;
    }
  }

  // Get bookings by service ID
  static async getByServiceId(serviceId) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('serviceId', '==', serviceId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting service bookings:', error);
      throw error;
    }
  }

  // Update booking
  static async update(bookingId, updateData) {
    try {
      const docRef = doc(db, COLLECTION_NAME, bookingId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });
      return { id: bookingId, ...updateData };
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    }
  }

  // Update booking status
  static async updateStatus(bookingId, status) {
    return this.update(bookingId, { status });
  }

  // Accept booking (provider action)
  static async accept(bookingId) {
    return this.updateStatus(bookingId, BookingStatus.ACCEPTED);
  }

  // Start work on booking
  static async startWork(bookingId) {
    return this.updateStatus(bookingId, BookingStatus.IN_PROGRESS);
  }

  // Complete booking
  static async complete(bookingId) {
    return this.update(bookingId, { 
      status: BookingStatus.COMPLETED,
      completedAt: serverTimestamp(),
    });
  }

  // Cancel booking
  static async cancel(bookingId, reason = '') {
    return this.update(bookingId, { 
      status: BookingStatus.CANCELLED,
      cancelReason: reason,
      cancelledAt: serverTimestamp(),
    });
  }

  // Delete booking (only pending)
  static async delete(bookingId) {
    try {
      const booking = await this.getById(bookingId);
      if (booking?.status !== BookingStatus.PENDING) {
        throw new Error('Only pending bookings can be deleted');
      }
      const docRef = doc(db, COLLECTION_NAME, bookingId);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  }

  // Get pending bookings count for provider
  static async getPendingCount(providerId) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('providerId', '==', providerId),
        where('status', '==', BookingStatus.PENDING)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error) {
      console.error('Error getting pending count:', error);
      throw error;
    }
  }

  // Get active bookings (accepted or in progress)
  static async getActive(providerId) {
    try {
      const bookings = await this.getByProviderId(providerId);
      return bookings.filter(b => 
        b.status === BookingStatus.ACCEPTED || 
        b.status === BookingStatus.IN_PROGRESS
      );
    } catch (error) {
      console.error('Error getting active bookings:', error);
      throw error;
    }
  }
}

export default BookingsService;